/**
 * Web Component <deadbug-editor>.
 *
 * API pública (atributos HTML):
 *  - project-url            : URL a un archivo .deadbug.json a cargar al arrancar.
 *  - component-library-url  : URL base donde vive la biblioteca de componentes.
 *                             Por defecto "/components/".
 *  - readonly               : "true" | ausente. Deshabilita edición.
 *
 * Nada en top-level accede a window/document: todo ocurre en connectedCallback.
 * Compatible con SSR de Astro.
 */

import { createEmptyProject, type Project } from '../core/model/project.js';
import { CanvasStage, type Selection } from '../view/stage.js';
import { ComponentLibrary, type LibraryIndexEntry } from '../core/library/loader.js';
import { serializeProject, deserializeProject, SerializationError } from '../core/persistence/serializer.js';
import { ComponentPreviewCache } from '../view/renderers/component-preview.js';

const OBSERVED_ATTRS = ['project-url', 'component-library-url', 'readonly'] as const;

/**
 * SVG icons inline (16×16 salvo indicación) para todos los botones de
 * la interfaz. Toda la UI (menús, toolbar, status, rejilla) usa la
 * paleta naranja + negro del logo de OpenSchematics; los renderers de
 * componentes / cables / soldaduras NO usan estas constantes: cada uno
 * conserva sus propios colores.
 */
// Paleta única de la UI. Los iconos siguen la regla: SILUETA en
// `UI.orange`; LÍNEAS INTERIORES en `UI.ink`; iconos SIN silueta
// (flechas, ejes, cruz) → todo `UI.orange`.
const UI = {
  orange: '#d9700f',
  ink: '#1a1a1a',
} as const;

// Regla general de color en los iconos:
//   - Silueta / borde exterior de la forma → NARANJA (UI.orange).
//   - Líneas interiores dentro de esa silueta → NEGRO (UI.ink).
//   - Iconos sin borde exterior (flechas, ejes, etc.) → todo NARANJA.
//   - Única excepción: el icono de soldadura, que se dibuja como una
//     bola metálica con su propio gradiente y se mantiene tal cual.
const ICONS = {
  save: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path stroke="${UI.orange}" d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><polyline stroke="${UI.ink}" points="17 21 17 13 7 13 7 21"/><polyline stroke="${UI.ink}" points="7 3 7 8 15 8"/></svg>`,
  open: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z"/></svg>`,
  image: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect stroke="${UI.orange}" x="3" y="4" width="18" height="16" rx="2"/><circle stroke="none" cx="9" cy="10" r="1.6" fill="${UI.ink}"/><path stroke="${UI.ink}" d="m4 18 5-5 4 4 3-3 4 4"/></svg>`,
  // Transistor NPN dentro de un anillo naranja: el mismo símbolo que
  // lleva la "O" del logo de OpenSchematics. Los tres terminales
  // atraviesan el anillo, igual que en el original.
  // La flecha del emisor apunta hacia ABAJO-DERECHA (sentido de la
  // corriente en un NPN: sale del emisor). La hacemos claramente
  // grande — se lee incluso a 16 px — y el trazo del emisor termina
  // ANTES de la flecha para que ésta no quede fundida con la línea.
  // Anillo naranja + símbolo NPN negro escalado al 58 % para caber
  // holgadamente dentro del círculo (radio 9 con trazo 2.4 →
  // interior útil hasta ~7.8): ni el símbolo cruza el anillo ni éste
  // aplasta al símbolo.
  component: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="${UI.orange}" stroke-width="2.4"/><g stroke="${UI.ink}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="5.97" y1="12" x2="10.38" y2="12"/><line x1="10.38" y1="8.64" x2="10.38" y2="15.36"/><line x1="10.38" y1="9.04" x2="15.83" y2="6.78"/><line x1="10.38" y1="14.96" x2="13.51" y2="16.55"/></g><polygon points="16.99,18.03 12.52,17.42 14.44,13.93" fill="${UI.ink}"/></svg>`,
  // Onda cuadrada del cable: no tiene "borde exterior" → todo naranja.
  wire: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12h6l3-6 3 12 3-6h3"/></svg>`,
  // Excepción declarada por el usuario: bola metálica con gradiente
  // propio, no sigue la regla naranja/negro. OJO — se usa a través
  // de `solderIcon()` para dar un id de gradient ÚNICO por instancia.
  // Si dos SVG en el mismo shadow root comparten `id="dbg-solder"`,
  // el navegador puede resolver `url(#dbg-solder)` de uno al `<defs>`
  // del otro, y si ese otro está en un dropdown oculto el gradient
  // se queda sin pintar hasta que el dropdown se abre.
  solder: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><defs><radialGradient id="__solder__" cx="35%" cy="35%" r="65%"><stop offset="0" stop-color="#ffffff"/><stop offset="0.5" stop-color="#a9adb3"/><stop offset="1" stop-color="#3a3d40"/></radialGradient></defs><circle cx="12" cy="12" r="7" fill="url(#__solder__)" stroke="#2b2d2f" stroke-width="1"/></svg>`,
  // Lápiz (silueta) naranja, línea de subrayado interior negra.
  properties: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path stroke="${UI.ink}" d="M12 20h9"/><path stroke="${UI.orange}" d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"/></svg>`,
  // Flechas curvas: sin borde exterior → todo naranja.
  rotateCcw: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>`,
  rotateCw: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></svg>`,
  // Flip: dos flechas laterales naranjas + eje CENTRAL negro (marca el
  // plano de simetría respecto al que se refleja).
  flip: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path stroke="${UI.ink}" d="M12 3v18"/><path stroke="${UI.orange}" d="M8 7 4 12l4 5"/><path stroke="${UI.orange}" d="m16 7 4 5-4 5"/></svg>`,
  // Tijera: los dos anillos son "silueta" (naranja), las tres líneas
  // de la hoja son interiores (negro).
  cut: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle stroke="${UI.orange}" cx="6" cy="6" r="3"/><circle stroke="${UI.orange}" cx="6" cy="18" r="3"/><line stroke="${UI.ink}" x1="20" y1="4" x2="8.12" y2="15.88"/><line stroke="${UI.ink}" x1="14.47" y1="14.48" x2="20" y2="20"/><line stroke="${UI.ink}" x1="8.12" y1="8.12" x2="12" y2="12"/></svg>`,
  // Flecha circular: sin borde exterior → todo naranja.
  restore: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 3 3 9 9 9"/></svg>`,
  // Rectángulo (silueta) naranja, "L" del cuadrado de atrás en negro.
  duplicate: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect stroke="${UI.orange}" x="9" y="9" width="12" height="12" rx="2"/><path stroke="${UI.ink}" d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`,
  // Papelera: tapa + cuerpo + asa = silueta naranja; las rayas del
  // interior en negro.
  trash: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline stroke="${UI.orange}" points="3 6 5 6 21 6"/><path stroke="${UI.orange}" d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path stroke="${UI.orange}" d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line stroke="${UI.ink}" x1="10" y1="11" x2="10" y2="17"/><line stroke="${UI.ink}" x1="14" y1="11" x2="14" y2="17"/></svg>`,
  // Front / Back: dos cuadrados solapados donde el SOMBREADO marca a
  // cuál afecta la acción. En "Front" es el de abajo-derecha; en
  // "Back", el de arriba-izquierda. El sombreado se dibuja el último
  // para que su contorno se vea entero y no haya ambigüedad sobre
  // cuál de los dos está destacado.
  // Front: el cuadrado destacado (delantero, abajo-derecha) es la
  // "silueta" que la acción trata — contorno + relleno naranja. El
  // cuadrado trasero pasa a ser un contorno auxiliar interior, negro.
  front: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect stroke="${UI.ink}" x="3" y="3" width="14" height="14" rx="1.5" fill="#ffffff"/><rect stroke="${UI.orange}" x="7" y="7" width="14" height="14" rx="1.5" fill="${UI.orange}" fill-opacity="0.75"/></svg>`,
  // Back: el cuadrado sombreado es el de arriba-izquierda, pero la
  // zona donde los dos cuadrados se solapan se deja sin sombrear —
  // así el cuadrado delantero (abajo-derecha) se percibe DELANTE del
  // sombreado, no detrás. Se pinta como un path en L (el cuadrado
  // trasero MENOS el trozo que solapa con el delantero) y encima se
  // vuelve a trazar el contorno del cuadrado trasero para que su
  // silueta se vea entera.
  // Back: el cuadrado sombreado es el de arriba-izquierda, pero la
  // zona donde los dos cuadrados se solapan se deja sin sombrear —
  // así el cuadrado delantero (abajo-derecha) se percibe DELANTE del
  // sombreado, no detrás. Se pinta como un path en L (el cuadrado
  // trasero MENOS el trozo que solapa con el delantero) y encima se
  // vuelve a trazar el contorno del cuadrado trasero, pero SÓLO su
  // parte visible fuera del delantero — para no dejar dos rayas
  // sueltas cruzando el cuadrado delantero por dentro.
  // Back: el cuadrado destacado ahora es el de atrás (arriba-izq).
  // Contorno + relleno naranja para él, contorno del delantero en
  // negro como línea auxiliar.
  back: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect stroke="${UI.ink}" x="7" y="7" width="14" height="14" rx="1.5" fill="#ffffff"/><path fill="${UI.orange}" fill-opacity="0.75" stroke="none" d="M 4.5 3 H 15.5 A 1.5 1.5 0 0 1 17 4.5 V 7 H 8.5 A 1.5 1.5 0 0 0 7 8.5 V 17 H 4.5 A 1.5 1.5 0 0 1 3 15.5 V 4.5 A 1.5 1.5 0 0 1 4.5 3 Z"/><path stroke="${UI.orange}" fill="none" d="M 17 7 V 4.5 A 1.5 1.5 0 0 0 15.5 3 H 4.5 A 1.5 1.5 0 0 0 3 4.5 V 15.5 A 1.5 1.5 0 0 0 4.5 17 H 7"/></svg>`,
  // Lupa: círculo + mango = silueta naranja; "+" / "−" interiores
  // en negro.
  zoomIn: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle stroke="${UI.orange}" cx="11" cy="11" r="7"/><line stroke="${UI.orange}" x1="21" y1="21" x2="16.65" y2="16.65"/><line stroke="${UI.ink}" x1="11" y1="8" x2="11" y2="14"/><line stroke="${UI.ink}" x1="8" y1="11" x2="14" y2="11"/></svg>`,
  zoomOut: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle stroke="${UI.orange}" cx="11" cy="11" r="7"/><line stroke="${UI.orange}" x1="21" y1="21" x2="16.65" y2="16.65"/><line stroke="${UI.ink}" x1="8" y1="11" x2="14" y2="11"/></svg>`,
  // Cuatro esquinas sueltas — sin silueta cerrada → todo naranja.
  fit: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8V4h4"/><path d="M21 8V4h-4"/><path d="M21 16v4h-4"/><path d="M3 16v4h4"/></svg>`,
  // Marco naranja, cuadrícula interior negra.
  grid: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect stroke="${UI.orange}" x="3" y="3" width="18" height="18" rx="1.5"/><line stroke="${UI.ink}" x1="3" y1="9" x2="21" y2="9"/><line stroke="${UI.ink}" x1="3" y1="15" x2="21" y2="15"/><line stroke="${UI.ink}" x1="9" y1="3" x2="9" y2="21"/><line stroke="${UI.ink}" x1="15" y1="3" x2="15" y2="21"/></svg>`,
  // Cruz de dos líneas — sin silueta → todo naranja.
  cancel: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>`,
  // Flechas de historial: sin silueta → todo naranja.
  undo: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-3a6 6 0 0 0-6-6H4"/></svg>`,
  redo: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${UI.orange}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 14 20 9 15 4"/><path d="M4 20v-3a6 6 0 0 1 6-6h10"/></svg>`,
  // Bend: flecha superior naranja (dirección del doblez) + trazo
  // representando el PIN doblándose en negro.
  bendLeft: /*svg*/`<svg width="16" height="16" viewBox="0 0 32 32" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line stroke="${UI.orange}" x1="6" y1="6" x2="24" y2="6" stroke-width="2.4"/><polyline stroke="${UI.orange}" points="11 2, 6 6, 11 10" stroke-width="2.4"/><path stroke="${UI.ink}" d="M16 30 V20 L8 12" stroke-width="2.6"/></svg>`,
  bendRight: /*svg*/`<svg width="16" height="16" viewBox="0 0 32 32" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line stroke="${UI.orange}" x1="8" y1="6" x2="26" y2="6" stroke-width="2.4"/><polyline stroke="${UI.orange}" points="21 2, 26 6, 21 10" stroke-width="2.4"/><path stroke="${UI.ink}" d="M16 30 V20 L24 12" stroke-width="2.6"/></svg>`,
  // Círculo (silueta) naranja + interrogación interior negra.
  help: /*svg*/`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle stroke="${UI.orange}" cx="12" cy="12" r="9"/><path stroke="${UI.ink}" d="M9.2 9a3 3 0 1 1 4 2.8c-.8.3-1.2 1-1.2 1.8v.4"/><line stroke="${UI.ink}" x1="12" y1="17.5" x2="12" y2="17.5"/></svg>`,
};

// Cada uso del icono de soldadura instancia el SVG con un id de
// gradient propio (`dbg-solder-1`, `dbg-solder-2`, …) para evitar el
// choque que hacía que el círculo se viese plano hasta abrir el menú.
let solderIconCounter = 0;
function solderIcon(): string {
  solderIconCounter += 1;
  const id = `dbg-solder-${solderIconCounter}`;
  return ICONS.solder.split('__solder__').join(id);
}

export class DeadBugEditorElement extends HTMLElement {
  static get observedAttributes(): readonly string[] {
    return OBSERVED_ATTRS;
  }

  private root!: ShadowRoot;
  private canvasContainer!: HTMLDivElement;
  private stage: CanvasStage | null = null;
  private library!: ComponentLibrary;
  private project: Project = createEmptyProject();
  /**
   * Nombre elegido por el usuario la última vez que guardó el esquema
   * (con extensión, p. ej. "esquema.dbe"). Se usa como sugerencia por
   * defecto en subsiguientes "Guardar" y en "Exportar imagen" mientras
   * el esquema esté abierto. Se limpia al abrir uno nuevo o cargar
   * otro fichero.
   */
  private savedFilename: string | null = null;
  private readonly previewCache = new ComponentPreviewCache();
  /** Fondo elegido la última vez que se exportó (preselección del diálogo). */
  private lastExportBackground: 'transparent' | 'white' = 'transparent';
  private lastPickedDefId: string | null = null;

  connectedCallback(): void {
    if (!this.shadowRoot) {
      this.root = this.attachShadow({ mode: 'open' });
    } else {
      this.root = this.shadowRoot;
    }
    this.render();
    this.mountStage();
    void this.bootstrap();
  }

  disconnectedCallback(): void {
    this.stage?.destroy();
    this.stage = null;
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.isConnected) return;
    // Placeholder: reaccionar a cambios de atributos cuando la vista esté conectada.
  }

  /** Estructura DOM básica del editor dentro del Shadow DOM. */
  private render(): void {
    this.root.innerHTML = `
      <style>
        :host {
          /* Paleta UI inspirada en el logo (naranja / negro). Todo lo
             que es "chrome" (menús, toolbar, status, rejilla) usa estas
             variables; los renderers de componentes/wires/soldaduras
             NO las tocan. */
          --ui-orange: #d9700f;
          --ui-orange-strong: #b85b00;
          --ui-orange-soft: #fef1e2;
          --ui-orange-border: #f3c88b;
          --ui-ink: #1a1a1a;
          --ui-text: #2b2b2b;
          --ui-text-mute: #6b5d4d;
          --ui-bg: #fbf6ef;
          --ui-bg-elev: #ffffff;
          --ui-border: #e6dbc8;
          --ui-border-strong: #cbbfa8;
          --ui-danger: #c53929;

          display: block;
          width: 100%;
          height: 100%;
          min-height: 400px;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
          color: var(--ui-text);
          background: var(--ui-bg-elev);
        }
        .editor {
          display: grid;
          grid-template-rows: auto auto 1fr auto;
          height: 100%;
        }
        /* En portrait añadimos una fila más (la barra de gestos), entre
           la toolbar y el canvas. */
        @media (orientation: portrait) {
          .editor { grid-template-rows: auto auto auto 1fr auto; }
        }
        .menu, .toolbar, .status {
          padding: 6px 10px;
          background: var(--ui-bg-elev);
          user-select: none;
        }
        .menu {
          border-bottom: 1px solid var(--ui-border);
          font-size: 13px;
          color: var(--ui-text);
          padding: 0 4px;
          display: flex;
          gap: 2px;
          position: relative;
          z-index: 9;
        }
        .menu-item { position: relative; }
        .menu-title {
          appearance: none;
          border: 0;
          background: none;
          font: inherit;
          font-size: 13px;
          color: var(--ui-ink);
          padding: 5px 10px;
          cursor: pointer;
          border-radius: 4px;
        }
        .menu-title:hover, .menu-item.open .menu-title {
          background: var(--ui-orange-soft);
          color: var(--ui-orange-strong);
        }
        .menu-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 210px;
          background: var(--ui-bg-elev);
          border: 1px solid var(--ui-border-strong);
          border-top: 2px solid var(--ui-orange);
          border-radius: 0 0 6px 6px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
          padding: 4px;
          display: none;
          z-index: 20;
        }
        .menu-item.open .menu-dropdown { display: block; }
        .menu-dropdown button {
          appearance: none;
          border: 0;
          background: none;
          font: inherit;
          font-size: 13px;
          color: var(--ui-text);
          width: 100%;
          text-align: left;
          padding: 6px 10px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }
        .menu-dropdown button:hover:not(:disabled) { background: var(--ui-orange-soft); }
        .menu-dropdown button:disabled { opacity: 0.4; cursor: not-allowed; }
        .menu-dropdown button svg { flex: 0 0 auto; width: 16px; height: 16px; }
        .menu-dropdown .shortcut {
          margin-left: auto;
          color: var(--ui-text-mute);
          font-size: 11px;
          padding-left: 18px;
        }
        .menu-sep { height: 1px; background: var(--ui-border); margin: 4px 6px; }
        .menu-spacer { flex: 1 1 0; min-width: 0; }
        .app-title {
          appearance: none;
          border: 0;
          background: none;
          font: inherit;
          font-weight: 800;
          font-size: 16px;
          letter-spacing: 0.5px;
          color: var(--ui-ink);
          padding: 4px 8px;
          margin-left: 16px;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
          flex: 0 0 auto;
        }
        .app-title .ac { color: var(--ui-orange); }
        .app-title .ai { color: var(--ui-ink); }
        .app-title:hover { opacity: 0.85; }
        .about-body { text-align: center; padding: 20px 24px; }
        .about-title {
          font-weight: 800;
          font-size: 22px;
          letter-spacing: 0.6px;
          margin-bottom: 8px;
        }
        .about-title .ac { color: var(--ui-orange); }
        .about-title .ai { color: var(--ui-ink); }
        .about-version {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 13px;
          color: var(--ui-orange-strong);
          margin-bottom: 12px;
        }
        .about-desc { margin: 0; color: var(--ui-text-mute); font-size: 13px; }
        .toolbar {
          display: flex;
          gap: 0;
          align-items: flex-end;
          border-bottom: 1px solid var(--ui-border);
          flex-wrap: wrap;
        }
        .toolbar-group {
          display: inline-flex;
          flex-direction: column;
          align-items: stretch;
          gap: 3px;
          padding: 0 10px;
          border-right: 1px solid var(--ui-border);
        }
        .toolbar-group:last-child { border-right: 0; }
        .toolbar-group-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: var(--ui-orange-strong);
          text-align: center;
          padding: 0 2px;
          font-weight: 600;
        }
        .toolbar-group-buttons {
          display: flex;
          gap: 4px;
          align-items: center;
        }
        .toolbar button {
          appearance: none;
          border: 1px solid var(--ui-border-strong);
          background: var(--ui-bg-elev);
          color: var(--ui-text);
          border-radius: 4px;
          padding: 0 8px;
          height: 28px;
          font: inherit;
          font-size: 13px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          line-height: 1;
          box-sizing: border-box;
          transition: background-color 90ms ease, border-color 90ms ease;
        }
        .toolbar button:hover:not(:disabled) {
          background: var(--ui-orange-soft);
          border-color: var(--ui-orange-border);
        }
        .toolbar button:disabled { opacity: 0.4; cursor: not-allowed; }
        .toolbar button svg { display: block; pointer-events: none; flex: 0 0 auto; width: 16px; height: 16px; }
        .toolbar button.icon-btn { padding: 0 6px; }
        .toolbar button.icon-btn svg { width: 20px; height: 20px; }
        .toolbar .spacer { flex: 1; }
        .modal-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        .modal-backdrop[hidden] { display: none; }
        .modal {
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
          min-width: 340px;
          max-width: 520px;
          max-height: 70vh;
          display: flex;
          flex-direction: column;
        }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          border-bottom: 1px solid #eee;
        }
        .modal-header h3 { margin: 0; font-size: 15px; font-weight: 600; }
        .modal-header button {
          appearance: none;
          border: none;
          background: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          padding: 0 4px;
        }
        .modal-body {
          padding: 10px 6px;
          overflow: auto;
          flex: 1;
        }
        .modal-footer {
          padding: 10px 14px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .modal-footer button {
          appearance: none;
          border: 1px solid #ccc;
          background: #fff;
          border-radius: 4px;
          padding: 6px 14px;
          font: inherit;
          font-size: 13px;
          cursor: pointer;
        }
        .modal-footer button.primary {
          background: var(--ui-orange);
          color: #fff;
          border-color: var(--ui-orange-strong);
        }
        .modal-footer button.primary:hover:not(:disabled) {
          background: var(--ui-orange-strong);
        }
        .modal-footer button:disabled { opacity: 0.4; cursor: not-allowed; }
        .component-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .component-list li {
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .component-list li:hover { background: var(--ui-orange-soft); }
        .component-list li.selected {
          background: var(--ui-orange-soft);
          outline: 1px solid var(--ui-orange);
        }
        .component-list .thumb {
          width: 48px;
          height: 48px;
          flex: 0 0 48px;
          background: #fafafa;
          border: 1px solid var(--ui-border);
          border-radius: 4px;
          object-fit: contain;
          display: block;
        }
        .component-list .info { flex: 1 1 auto; min-width: 0; }
        .component-list .name { font-weight: 500; }
        .component-list .meta { color: var(--ui-text-mute); font-size: 11px; margin-top: 2px; }
        .shortcuts {
          border-collapse: collapse;
          width: 100%;
          font-size: 13px;
        }
        .shortcuts th, .shortcuts td {
          text-align: left;
          vertical-align: top;
          padding: 4px 10px;
          font-weight: 400;
        }
        .shortcuts th {
          white-space: nowrap;
          width: 1%;
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 12px;
          color: var(--ui-orange-strong);
        }
        .shortcuts td { color: var(--ui-text); }
        .shortcuts tr:nth-child(even) { background: var(--ui-bg); }
        .export-hint { margin: 0 8px 10px; font-size: 13px; color: var(--ui-text); }
        .bg-choice { display: flex; flex-direction: column; gap: 6px; padding: 0 8px; }
        .bg-choice label {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border: 1px solid var(--ui-border);
          border-radius: 6px;
          cursor: pointer;
        }
        .bg-choice label:hover { background: var(--ui-orange-soft); }
        .bg-choice label:has(input:checked) {
          background: var(--ui-orange-soft);
          border-color: var(--ui-orange);
        }
        .bg-choice .swatch {
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          border: 1px solid #bbb;
          border-radius: 4px;
        }
        .bg-choice .swatch.white { background: #fff; }
        .bg-choice .swatch.transparent {
          background-color: #fff;
          background-image:
            linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%),
            linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%);
          background-size: 12px 12px;
          background-position: 0 0, 6px 6px;
        }
        .bg-choice .bg-text { display: flex; flex-direction: column; min-width: 0; }
        .bg-choice .bg-name { font-weight: 500; font-size: 13px; }
        .bg-choice .bg-desc { color: #888; font-size: 11px; margin-top: 2px; }
        .props-panel {
          position: absolute;
          top: 120px;
          left: 20px;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 6px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          min-width: 220px;
          font-size: 13px;
          z-index: 8;
          user-select: none;
        }
        .props-panel[hidden] { display: none; }
        .props-panel .head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f5f5f5;
          padding: 6px 10px;
          border-bottom: 1px solid #eee;
          border-radius: 6px 6px 0 0;
          cursor: move;
        }
        .props-panel .head strong { font-size: 12px; color: #333; }
        .props-panel .head button {
          appearance: none;
          border: none;
          background: none;
          font-size: 16px;
          cursor: pointer;
          color: #666;
          padding: 0 4px;
        }
        .props-panel .body {
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .props-panel label {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 12px;
          color: #555;
        }
        .props-panel input[type="text"] {
          padding: 4px 6px;
          border: 1px solid #bbb;
          border-radius: 4px;
          font: inherit;
          font-size: 13px;
        }
        .status {
          border-top: 1px solid var(--ui-border);
          font-size: 12px;
          color: var(--ui-text-mute);
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 2px 8px;
        }
        .status [data-role="status-text"] { flex: 1; }
        .status .zoom-indicator {
          font-variant-numeric: tabular-nums;
          color: var(--ui-orange-strong);
          background: var(--ui-orange-soft);
          border: 1px solid var(--ui-orange-border);
          padding: 1px 6px;
          border-radius: 3px;
        }
        .canvas {
          position: relative;
          overflow: hidden;
          background: #ffffff;
          outline: none;
        }
        /* La barra de gestos táctiles solo se muestra en portrait
           (móvil / tablet vertical). En escritorio el elemento existe
           pero está oculto y no ocupa sitio. */
        .touch-hint { display: none; }

        /* ------------------------------------------------------------
           Modo PORTRAIT (móvil / tablet vertical)
           - Toolbar en scroll horizontal, sin envolver: los grupos
             quedan uno detrás de otro y se accede a ellos deslizando
             el dedo.
           - Menú también scrollable horizontalmente para que Help /
             título nunca queden cortados.
           - Se muestra la barra de "gestos" arriba del canvas con las
             instrucciones básicas.
           - touch-action:none en el canvas para que las gestos
             (arrastrar / pellizcar) las gestione el editor y no el
             navegador (evita el bounce de scroll y el zoom nativo).
           ------------------------------------------------------------ */
        @media (orientation: portrait) {
          /* En móvil el menú NO puede tener overflow, porque las
             dropdowns son position:absolute y quedarían clipadas por
             el contenedor scrollable. Se hace hueco escondiendo el
             título (accesible por Help → Acerca de). */
          .menu-spacer, .app-title { display: none; }
          .menu { flex-wrap: nowrap; overflow: visible; }

          .toolbar {
            flex-wrap: nowrap;
            overflow-x: auto;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
          }
          .toolbar-group {
            flex: 0 0 auto;
          }
          /* Un pelín más grandes para dedo. */
          .toolbar button { height: 32px; min-width: 32px; }
          .toolbar button.icon-btn svg { width: 22px; height: 22px; }
          /* En portrait los botones se muestran SÓLO como icono para
             que quepan varios grupos en la barra. La etiqueta sigue
             existiendo en el DOM (accesibilidad, tooltip), pero se
             oculta visualmente. Igual con los labels de grupo, que
             ocuparían una fila extra sin aportar nada nuevo cuando
             el icono ya identifica la acción. */
          .toolbar .btn-label,
          .toolbar-group-label { display: none; }
          /* En móvil las acciones de archivo (guardar / abrir /
             exportar) sólo se ofrecen desde el menú File. Sacarlas
             de la toolbar deja hueco para los grupos que sí se usan
             a menudo mientras se dibuja. */
          .toolbar-group[data-group="file"] { display: none; }
          .toolbar-group { padding: 0 6px; }
          .toolbar button svg { width: 20px; height: 20px; }

          .touch-hint {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 4px 8px;
            font-size: 11px;
            color: var(--ui-orange-strong);
            background: var(--ui-orange-soft);
            border-bottom: 1px solid var(--ui-orange-border);
            flex-wrap: wrap;
          }
          .touch-hint span { white-space: nowrap; }

          .canvas { touch-action: none; }
        }
      </style>
      <div class="editor">
        <div class="menu" data-role="menu">
          <div class="menu-item" data-menu="file">
            <button class="menu-title" data-menu-toggle="file">File</button>
            <div class="menu-dropdown">
              <button data-action="save-project">${ICONS.save} Save<span class="shortcut">Ctrl+S</span></button>
              <button data-action="open-project">${ICONS.open} Open…</button>
              <button data-action="export-image">${ICONS.image} Export PNG…</button>
            </div>
          </div>
          <div class="menu-item" data-menu="edit">
            <button class="menu-title" data-menu-toggle="edit">Edit</button>
            <div class="menu-dropdown">
              <button data-action="undo" data-role="mi-undo">${ICONS.undo} Undo<span class="shortcut">Ctrl+Z</span></button>
              <button data-action="redo" data-role="mi-redo">${ICONS.redo} Redo<span class="shortcut">Ctrl+Y</span></button>
              <div class="menu-sep"></div>
              <button data-action="duplicate" data-needs="component">${ICONS.duplicate} Duplicate<span class="shortcut">Ctrl+C</span></button>
              <button data-action="delete" data-needs="deletable">${ICONS.trash} Delete<span class="shortcut">Del</span></button>
              <div class="menu-sep"></div>
              <button data-action="rotate-cw" data-needs="component">${ICONS.rotateCw} Rotate 90° CW<span class="shortcut">R</span></button>
              <button data-action="rotate-ccw" data-needs="component">${ICONS.rotateCcw} Rotate 90° CCW</button>
              <button data-action="flip" data-needs="component">${ICONS.flip} Flip<span class="shortcut">F</span></button>
              <div class="menu-sep"></div>
              <button data-action="bend-left" data-needs="bend">${ICONS.bendLeft} Bend left<span class="shortcut">←</span></button>
              <button data-action="bend-right" data-needs="bend">${ICONS.bendRight} Bend right<span class="shortcut">→</span></button>
              <button data-action="cut" data-needs="bend" data-role="mi-cut">${ICONS.cut} Cut</button>
              <button data-action="restore" data-needs="restorable">${ICONS.restore} Restore</button>
              <div class="menu-sep"></div>
              <button data-action="properties" data-needs="component">${ICONS.properties} Properties…</button>
            </div>
          </div>
          <div class="menu-item" data-menu="view">
            <button class="menu-title" data-menu-toggle="view">View</button>
            <div class="menu-dropdown">
              <button data-action="zoom-in">${ICONS.zoomIn} Zoom in</button>
              <button data-action="zoom-out">${ICONS.zoomOut} Zoom out</button>
              <button data-action="fit">${ICONS.fit} Fit to view</button>
              <button data-action="toggle-grid">${ICONS.grid} Show/hide grid</button>
              <div class="menu-sep"></div>
              <button data-action="move-top" data-needs="component">${ICONS.front} Bring to front</button>
              <button data-action="move-bottom" data-needs="component">${ICONS.back} Send to back</button>
            </div>
          </div>
          <div class="menu-item" data-menu="tools">
            <button class="menu-title" data-menu-toggle="tools">Tools</button>
            <div class="menu-dropdown">
              <button data-action="add-component">${ICONS.component} Add component<span class="shortcut">C</span></button>
              <button data-action="add-wire">${ICONS.wire} Add wire<span class="shortcut">W</span></button>
              <button data-action="add-solder">${solderIcon()} Add solder joint<span class="shortcut">S</span></button>
            </div>
          </div>
          <div class="menu-item" data-menu="help">
            <button class="menu-title" data-menu-toggle="help">Help</button>
            <div class="menu-dropdown">
              <button data-action="shortcuts">${ICONS.help} Keyboard shortcuts</button>
            </div>
          </div>
          <!-- Título justo a la derecha del último menú (Help). Sin
               separador antes queda pegado a Help; el separador
               posterior con flex:1 absorbe todo el hueco sobrante
               hasta el borde derecho de la barra. -->
          <button class="app-title" data-action="about" title="About DeadBug">
            <span class="ac">D</span><span class="ai">EA</span><span class="ai">D</span><span class="ac">B</span><span class="ai">UG</span>&nbsp;<span class="ac">E</span><span class="ai">LECTRONICS</span>&nbsp;<span class="ac">E</span><span class="ai">DITOR</span>
          </button>
          <span class="menu-spacer" aria-hidden="true"></span>
        </div>
        <div class="toolbar" data-role="toolbar">
          <div class="toolbar-group" data-group="file">
            <div class="toolbar-group-label">Schematic</div>
            <div class="toolbar-group-buttons">
              <button data-action="save-project" title="Save schematic to a .dbe file">${ICONS.save}<span class="btn-label">Save</span></button>
              <button data-action="open-project" title="Open a .dbe file">${ICONS.open}<span class="btn-label">Open</span></button>
              <button data-action="export-image" title="Export schematic as a PNG image">${ICONS.image}<span class="btn-label">Export PNG</span></button>
            </div>
          </div>
          <div class="toolbar-group">
            <div class="toolbar-group-label">Add</div>
            <div class="toolbar-group-buttons">
              <button data-action="add-component" title="Add component (C, P)">${ICONS.component}<span class="btn-label">Component</span></button>
              <button data-action="add-wire" title="Add wire (W)" data-role="btn-wire">${ICONS.wire}<span class="btn-label">Wire</span></button>
              <button data-action="add-solder" title="Add solder joint at a bend point or endpoint (S)" data-role="btn-solder">${solderIcon()}<span class="btn-label">Solder</span></button>
              <button data-action="properties"  title="Edit properties (name, PN)"  data-needs="component">${ICONS.properties}<span class="btn-label">Properties</span></button>
            </div>
          </div>
          <div class="toolbar-group">
            <div class="toolbar-group-label">Edit</div>
            <div class="toolbar-group-buttons">
              <button data-action="rotate-ccw" title="Rotate 90° counter-clockwise" data-needs="component">${ICONS.rotateCcw}<span class="btn-label">90°</span></button>
              <button data-action="rotate-cw" title="Rotate 90° clockwise (R, Space)" data-needs="component">${ICONS.rotateCw}<span class="btn-label">90°</span></button>
              <button class="icon-btn" data-action="bend-left"  title="Bend (Left / Down key)" data-needs="bend" aria-label="Bend left">${ICONS.bendLeft}</button>
              <button class="icon-btn" data-action="bend-right" title="Bend (Right / Up key)" data-needs="bend" aria-label="Bend right">${ICONS.bendRight}</button>
              <button data-action="flip" title="Flip horizontally (F)" data-needs="component">${ICONS.flip}<span class="btn-label">Flip</span></button>
              <button data-action="cut"        title="Cut pin at this point" data-needs="bend" data-role="btn-cut">${ICONS.cut}<span class="btn-label">Cut</span></button>
              <button data-action="restore"    title="Restore" data-needs="restorable" data-role="btn-restore">${ICONS.restore}<span class="btn-label">Restore</span></button>
              <button data-action="duplicate"   title="Duplicate (Ctrl+C)" data-needs="component">${ICONS.duplicate}<span class="btn-label">Duplicate</span></button>
              <button data-action="delete"      title="Delete (Del)" data-needs="deletable">${ICONS.trash}<span class="btn-label">Delete</span></button>
            </div>
          </div>
          <div class="toolbar-group">
            <div class="toolbar-group-label">View</div>
            <div class="toolbar-group-buttons">
              <button data-action="move-top"    title="Bring to front"        data-needs="component">${ICONS.front}<span class="btn-label">Front</span></button>
              <button data-action="move-bottom" title="Send to back"          data-needs="component">${ICONS.back}<span class="btn-label">Back</span></button>
              <button data-action="zoom-out" title="Zoom out">${ICONS.zoomOut}</button>
              <button data-action="zoom-in" title="Zoom in">${ICONS.zoomIn}</button>
              <button data-action="fit" title="Fit to view">${ICONS.fit}<span class="btn-label">Fit</span></button>
              <button data-action="toggle-grid" title="Show/hide grid (2.54 mm)" data-role="btn-grid">${ICONS.grid}<span class="btn-label">Grid</span></button>
            </div>
          </div>
        </div>
        <div class="touch-hint" aria-hidden="true">
          <span>👆 Tap = select</span>
          <span>✋ Drag = move</span>
          <span>🤏 Pinch = zoom</span>
          <span>✌️ 2-finger drag = pan</span>
        </div>
        <div class="canvas" data-role="canvas" tabindex="0"></div>
        <input type="file" data-role="file-picker" accept=".dbe,.json,application/json" hidden />
        <a data-role="file-download" hidden></a>
        <div class="status" data-role="status">
          <span data-role="status-text">Ready.</span>
          <span data-role="zoom-indicator" class="zoom-indicator">zoom 100 %</span>
        </div>
        <div class="props-panel" data-role="props-panel" hidden>
          <div class="head" data-role="props-head">
            <strong>Properties</strong>
            <button data-action="close-props" title="Close">×</button>
          </div>
          <div class="body">
            <label>
              Part number / value
              <input type="text" data-role="props-value" />
            </label>
            <div data-role="props-extra"></div>
            <div class="meta" data-role="props-uid" style="font-size:11px;color:#999"></div>
          </div>
        </div>
        <div class="modal-backdrop" data-role="library-backdrop" hidden>
          <div class="modal" role="dialog" aria-label="Component library">
            <div class="modal-header">
              <h3>Add component</h3>
              <button data-action="close-library" title="Close">×</button>
            </div>
            <div class="modal-body">
              <ul class="component-list" data-role="component-list"></ul>
            </div>
            <div class="modal-footer">
              <button data-action="close-library">Cancel</button>
              <button data-action="add-selected" class="primary" data-role="add-btn" disabled>Add</button>
            </div>
          </div>
        </div>
        <div class="modal-backdrop" data-role="about-backdrop" hidden>
          <div class="modal" role="dialog" aria-label="About DeadBug">
            <div class="modal-header">
              <h3>About</h3>
              <button data-action="close-about" title="Close">×</button>
            </div>
            <div class="modal-body about-body">
              <div class="about-title">
                <span class="ac">D</span><span class="ai">EA</span><span class="ai">D</span><span class="ac">B</span><span class="ai">UG</span>&nbsp;<span class="ac">E</span><span class="ai">LECTRONICS</span>&nbsp;<span class="ac">E</span><span class="ai">DITOR</span>
              </div>
              <div class="about-version">version 1.0.0</div>
              <p class="about-desc">Free-form dead-bug schematic editor.</p>
            </div>
            <div class="modal-footer">
              <button data-action="close-about" class="primary">Close</button>
            </div>
          </div>
        </div>
        <div class="modal-backdrop" data-role="shortcuts-backdrop" hidden>
          <div class="modal" role="dialog" aria-label="Keyboard shortcuts">
            <div class="modal-header">
              <h3>Keyboard shortcuts</h3>
              <button data-action="close-shortcuts" title="Close">×</button>
            </div>
            <div class="modal-body" data-role="shortcuts-body"></div>
            <div class="modal-footer">
              <button data-action="close-shortcuts" class="primary">Close</button>
            </div>
          </div>
        </div>
        <div class="modal-backdrop" data-role="export-backdrop" hidden>
          <div class="modal" role="dialog" aria-label="Export image options">
            <div class="modal-header">
              <h3>Export PNG</h3>
              <button data-action="close-export" title="Close">×</button>
            </div>
            <div class="modal-body">
              <p class="export-hint">Choose the image background:</p>
              <div class="bg-choice" data-role="bg-choice">
                <label>
                  <input type="radio" name="export-bg" value="transparent" checked />
                  <span class="swatch transparent"></span>
                  <span class="bg-text">
                    <span class="bg-name">Transparent</span>
                    <span class="bg-desc">Keeps the alpha channel — good for overlaying on other documents.</span>
                  </span>
                </label>
                <label>
                  <input type="radio" name="export-bg" value="white" />
                  <span class="swatch white"></span>
                  <span class="bg-text">
                    <span class="bg-name">White</span>
                    <span class="bg-desc">Solid white background — good for printing or sharing.</span>
                  </span>
                </label>
              </div>
            </div>
            <div class="modal-footer">
              <button data-action="close-export">Cancel</button>
              <button data-action="export-confirm" class="primary">Export</button>
            </div>
          </div>
        </div>
      </div>
    `;
    this.canvasContainer = this.root.querySelector('[data-role="canvas"]') as HTMLDivElement;
    this.wireToolbar();
    this.wireKeyboardShortcuts();
  }

  private wireToolbar(): void {
    const toolbar = this.root.querySelector('[data-role="toolbar"]') as HTMLDivElement;
    toolbar.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.closest<HTMLElement>('[data-action]')?.dataset['action'];
      if (action) this.runAction(action);
    });
    this.wireMenus();

    // Fallback del <input type="file"> para navegadores sin File
    // System Access API (o cuando el usuario cancela el picker).
    const picker = this.root.querySelector('[data-role="file-picker"]') as HTMLInputElement;
    picker.addEventListener('change', () => {
      const file = picker.files?.[0];
      picker.value = ''; // permitir reabrir el mismo fichero de nuevo
      if (file) void this.loadProjectFile(file);
    });

    // Botones internos del modal.
    const backdrop = this.root.querySelector('[data-role="library-backdrop"]') as HTMLDivElement;
    backdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === backdrop) {
        this.closeLibraryModal();
        return;
      }
      const action = target.closest<HTMLElement>('[data-action]')?.dataset['action'];
      if (action === 'close-library') this.closeLibraryModal();
      else if (action === 'add-selected') void this.confirmLibraryPick();
    });

    // Modal "About" (título + versión).
    const aboutBackdrop = this.root.querySelector('[data-role="about-backdrop"]') as HTMLDivElement;
    aboutBackdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === aboutBackdrop
        || target.closest<HTMLElement>('[data-action]')?.dataset['action'] === 'close-about') {
        this.closeAbout();
      }
    });
    aboutBackdrop.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.code === 'Escape' || ev.code === 'Enter' || ev.code === 'NumpadEnter') {
        ev.preventDefault();
        this.closeAbout();
      }
    });

    // Modal de atajos de teclado.
    const shortcutsBackdrop = this.root.querySelector('[data-role="shortcuts-backdrop"]') as HTMLDivElement;
    shortcutsBackdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === shortcutsBackdrop
        || target.closest<HTMLElement>('[data-action]')?.dataset['action'] === 'close-shortcuts') {
        this.closeShortcuts();
      }
    });
    shortcutsBackdrop.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.code === 'Escape' || ev.code === 'Enter' || ev.code === 'NumpadEnter') {
        ev.preventDefault();
        this.closeShortcuts();
      }
    });

    // Modal de opciones de exportación (fondo blanco / transparente).
    const exportBackdrop = this.root.querySelector('[data-role="export-backdrop"]') as HTMLDivElement;
    exportBackdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === exportBackdrop) {
        this.closeExportModal();
        return;
      }
      const action = target.closest<HTMLElement>('[data-action]')?.dataset['action'];
      if (action === 'close-export') this.closeExportModal();
      else if (action === 'export-confirm') void this.confirmExport();
    });
    exportBackdrop.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.code === 'Escape') {
        ev.preventDefault();
        this.closeExportModal();
      } else if (ev.code === 'Enter' || ev.code === 'NumpadEnter') {
        ev.preventDefault();
        void this.confirmExport();
      }
    });
    this.updateSelectionUi(null);
  }

  /**
   * Menús desplegables de la barra superior. Comparten las mismas
   * acciones (`data-action`) que los botones de la toolbar, así que
   * ambos desembocan en `runAction`.
   */
  private wireMenus(): void {
    const menu = this.root.querySelector('[data-role="menu"]') as HTMLDivElement;

    const closeAll = () => {
      menu.querySelectorAll('.menu-item.open').forEach((el) => el.classList.remove('open'));
    };

    menu.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const toggle = target.closest<HTMLElement>('[data-menu-toggle]');
      if (toggle) {
        const item = toggle.parentElement!;
        const wasOpen = item.classList.contains('open');
        closeAll();
        if (!wasOpen) {
          // Estado de los items que dependen de la selección o del
          // historial: se evalúa al abrir, no hace falta mantenerlo vivo.
          this.refreshMenuState();
          item.classList.add('open');
        }
        return;
      }
      const btn = target.closest<HTMLElement>('[data-action]');
      if (!btn) return;
      if ((btn as HTMLButtonElement).disabled) return;
      closeAll();
      this.runAction(btn.dataset['action']!);
    });

    // Hover entre menús cuando ya hay uno abierto (comportamiento
    // habitual de una barra de menús).
    menu.addEventListener('mouseover', (e) => {
      if (!menu.querySelector('.menu-item.open')) return;
      const toggle = (e.target as HTMLElement).closest<HTMLElement>('[data-menu-toggle]');
      if (!toggle) return;
      closeAll();
      this.refreshMenuState();
      toggle.parentElement!.classList.add('open');
    });

    // Cerrar al hacer clic fuera o con Escape.
    this.root.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('[data-role="menu"]')) closeAll();
    });
    this.root.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).code === 'Escape') closeAll();
    });
  }

  /** Habilita/deshabilita los items de menú según selección e historial. */
  private refreshMenuState(): void {
    const menu = this.root.querySelector('[data-role="menu"]');
    if (!menu) return;
    const selection = this.stage?.getSelection() ?? null;
    const editablePin = this.isSelectedPinEditable(selection);
    menu.querySelectorAll<HTMLButtonElement>('[data-needs]').forEach((b) => {
      b.disabled = !matchesNeed(b.dataset['needs'], selection, editablePin);
    });
    const undoBtn = menu.querySelector<HTMLButtonElement>('[data-role="mi-undo"]');
    if (undoBtn) undoBtn.disabled = !(this.stage?.canUndo() ?? false);
    const redoBtn = menu.querySelector<HTMLButtonElement>('[data-role="mi-redo"]');
    if (redoBtn) redoBtn.disabled = !(this.stage?.canRedo() ?? false);
    const cutItem = menu.querySelector<HTMLButtonElement>('[data-role="mi-cut"]');
    if (cutItem) {
      const isWireBend = selection?.kind === 'wire-bend';
      cutItem.innerHTML = `${ICONS.cut} ${isWireBend ? 'Split wire' : 'Cut'}`;
    }
  }

  /** Despacha una acción, venga de la toolbar, de un menú o del teclado. */
  private runAction(action: string): void {
    {
      switch (action) {
        case 'zoom-in':
          this.stage?.zoomIn();
          break;
        case 'zoom-out':
          this.stage?.zoomOut();
          break;
        case 'fit':
          this.stage?.fit();
          break;
        case 'rotate-cw':
          this.stage?.rotateSelected(90);
          break;
        case 'rotate-ccw':
          this.stage?.rotateSelected(-90);
          break;
        case 'flip':
          this.stage?.flipSelected();
          break;
        case 'bend-left':
          this.doBendResolved('left');
          break;
        case 'bend-right':
          this.doBendResolved('right');
          break;
        case 'cut':
          this.stage?.cutSelected();
          break;
        case 'restore': {
          const sel = this.stage?.getSelection();
          if (sel?.kind === 'component') this.stage?.restoreComponent();
          else this.stage?.restoreSelected();
          break;
        }
        case 'move-top':
          this.stage?.moveSelectedToTop();
          break;
        case 'move-bottom':
          this.stage?.moveSelectedToBottom();
          break;
        case 'properties': {
          const sel = this.stage?.getSelection();
          // Panel de propiedades sólo tiene sentido para un único
          // componente; se ignora en multi-selección y en cables.
          if (sel && (sel.kind === 'component' || sel.kind === 'pin' || sel.kind === 'bend')) {
            this.openPropertiesPanel(sel.component_uid);
          }
          break;
        }
        case 'duplicate':
          this.stage?.duplicateSelected();
          break;
        case 'delete':
          this.stage?.deleteSelected();
          break;
        case 'toggle-grid': {
          const visible = this.stage?.toggleGrid() ?? false;
          this.updateToggleButton('btn-grid', visible);
          break;
        }
        case 'add-component':
          void this.openLibraryModal();
          break;
        case 'add-wire':
          if (this.stage?.isPlacingWire()) this.stage.cancelWirePlacement();
          else this.stage?.startWirePlacement();
          break;
        case 'add-solder':
          if (this.stage?.isPlacingSolder()) this.stage.cancelSolderPlacement();
          else this.stage?.startSolderPlacement();
          break;
        case 'save-project':
          void this.saveProjectToFile();
          break;
        case 'open-project':
          void this.openProjectFromFile();
          break;
        case 'export-image':
          this.openExportModal();
          break;
        case 'undo':
          this.stage?.undo();
          break;
        case 'redo':
          this.stage?.redo();
          break;
        case 'shortcuts':
          this.showShortcuts();
          break;
        case 'about':
          this.openAbout();
          break;
      }
    }
  }

  /**
   * Atajos, en una tabla de dos columnas (tecla / acción). Antes era un
   * `alert()` con el texto alineado a base de espacios, que sólo cuadra
   * con fuente monoespaciada — y la del diálogo nativo no lo es.
   */
  private showShortcuts(): void {
    const rows: [string, string][] = [
      ['R · Space', 'Rotate 90° clockwise'],
      ['F', 'Flip'],
      ['C · P', 'Add component'],
      ['W', 'Add wire'],
      ['S', 'Add solder joint'],
      ['T', 'Cut pin / split wire at selected bend'],
      ['Ctrl+C', 'Duplicate'],
      ['Ctrl+V', 'Drop what is being placed'],
      ['Ctrl+S', 'Save schematic'],
      ['Ctrl+Z', 'Undo'],
      ['Ctrl+Y', 'Redo'],
      ['Arrows', 'Bend (with a bend point selected), otherwise move one grid step'],
      ['Del', 'Delete'],
      ['Esc', 'Cancel current operation / clear selection'],
      ['Space + drag', 'Pan'],
      ['Wheel', 'Zoom'],
    ];
    const body = this.root.querySelector('[data-role="shortcuts-body"]') as HTMLDivElement;
    body.innerHTML = `<table class="shortcuts">${rows
      .map(([k, d]) => `<tr><th>${escapeHtml(k)}</th><td>${escapeHtml(d)}</td></tr>`)
      .join('')}</table>`;
    const backdrop = this.root.querySelector('[data-role="shortcuts-backdrop"]') as HTMLDivElement;
    backdrop.hidden = false;
    backdrop.querySelector<HTMLButtonElement>('[data-action="close-shortcuts"]')?.focus();
  }

  private closeShortcuts(): void {
    const backdrop = this.root.querySelector('[data-role="shortcuts-backdrop"]') as HTMLDivElement;
    backdrop.hidden = true;
    this.canvasContainer.focus();
  }

  private openAbout(): void {
    const backdrop = this.root.querySelector('[data-role="about-backdrop"]') as HTMLDivElement;
    backdrop.hidden = false;
    backdrop.querySelector<HTMLButtonElement>('[data-action="close-about"].primary')?.focus();
  }

  private closeAbout(): void {
    const backdrop = this.root.querySelector('[data-role="about-backdrop"]') as HTMLDivElement;
    backdrop.hidden = true;
    this.canvasContainer.focus();
  }

  /**
   * Atajos de teclado (registrados en el shadow root para no colisionar
   * con el resto de la página):
   *   R / Space          → rotar 90° horario (elemento seleccionado)
   *   F                  → invertir
   *   Ctrl+C             → duplicar
   *   Ctrl+V             → sólo durante duplicar/colocación, fija la
   *                        colocación en la posición actual (equivale
   *                        al click). Fuera de placing: no hace nada
   *                        (deja al sistema pegar texto en inputs).
   *   C / P              → añadir componente
   *   W                  → añadir cable
   *   S                  → añadir soldadura
   *   ↑ / →              → doblar horario (si bend simple seleccionado)
   *                        o mover 1 paso arriba/derecha (si componente)
   *   ↓ / ←              → doblar antihorario o mover 1 paso abajo/izq
   *
   * En multi-selección los flechas siempre mueven (aunque haya un
   * bend en la selección). No hay auto-repeat: hace falta soltar y
   * volver a pulsar para cada paso (usamos keydown con `event.repeat`
   * ignorado).
   *
   * Todos los atajos se anulan si el foco está dentro de un input o
   * textarea del panel de propiedades / modal — así el usuario puede
   * teclear con normalidad (incluidos Ctrl+C/V del sistema).
   */
  private wireKeyboardShortcuts(): void {
    this.root.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      const target = ev.target as HTMLElement | null;
      // No secuestrar cuando el usuario está escribiendo en un input.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || (target as HTMLElement).isContentEditable)) {
        return;
      }
      // Ignorar auto-repeat: cada paso requiere soltar y re-pulsar.
      if (ev.repeat) return;
      const stage = this.stage;
      if (!stage) return;
      const sel = stage.getSelection();
      const withMod = ev.ctrlKey || ev.metaKey;

      // Deshacer / rehacer. Ctrl+Y y Ctrl+Shift+Z valen como rehacer.
      if (withMod && (ev.code === 'KeyZ' || ev.code === 'KeyY')) {
        ev.preventDefault();
        if (ev.code === 'KeyY' || ev.shiftKey) stage.redo();
        else stage.undo();
        return;
      }
      // Ctrl+S guarda.
      if (withMod && ev.code === 'KeyS') {
        ev.preventDefault();
        void this.saveProjectToFile();
        return;
      }
      // Ctrl+C / Ctrl+V
      if (withMod && (ev.code === 'KeyC' || ev.code === 'KeyV')) {
        if (ev.code === 'KeyC') {
          if (sel && (sel.kind === 'component' || sel.kind === 'pin' || sel.kind === 'bend'
                      || sel.kind === 'multi' || sel.kind === 'wire' || sel.kind === 'wire-bend')) {
            ev.preventDefault();
            stage.duplicateSelected();
          }
        } else {
          if (stage.isPlacing()) {
            ev.preventDefault();
            stage.confirmPlacement();
          }
        }
        return;
      }
      // Cualquier otro atajo con modificadores: no interceptar.
      if (withMod || ev.altKey) return;

      switch (ev.code) {
        case 'KeyR':
        case 'Space':
          ev.preventDefault();
          stage.rotateSelected(90);
          break;
        case 'KeyF':
          ev.preventDefault();
          stage.flipSelected();
          break;
        case 'KeyC':
        case 'KeyP':
          ev.preventDefault();
          void this.openLibraryModal();
          break;
        case 'KeyW':
          ev.preventDefault();
          if (stage.isPlacingWire()) stage.cancelWirePlacement();
          else stage.startWirePlacement();
          break;
        case 'KeyS':
          ev.preventDefault();
          if (stage.isPlacingSolder()) stage.cancelSolderPlacement();
          else stage.startSolderPlacement();
          break;
        case 'KeyT':
          // Corta/divide en el punto de doblez seleccionado:
          //   - pin (bend seleccionado) → recorta el pin en ese bend.
          //   - cable (wire-bend seleccionado) → divide el cable en dos.
          // Sin bend seleccionado no hace nada.
          if (sel && (sel.kind === 'bend' || sel.kind === 'wire-bend')) {
            ev.preventDefault();
            stage.cutSelected();
          }
          break;
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          if (!sel) return;
          const isSingleBend = sel.kind === 'bend' || sel.kind === 'wire-bend';
          if (isSingleBend) {
            // Analogo a los botones de la toolbar: ↑/→ = botón derecha,
            // ↓/← = botón izquierda. La dirección real (CW/CCW) se
            // resuelve según el ángulo mundo del tramo.
            const buttonDir: 'left' | 'right' =
              (ev.code === 'ArrowUp' || ev.code === 'ArrowRight') ? 'right' : 'left';
            ev.preventDefault();
            this.doBendResolved(buttonDir);
          } else if (sel.kind === 'component' || sel.kind === 'multi' || sel.kind === 'wire') {
            // Mover 1 paso de rejilla. En multi con bend simplemente
            // no llega aquí porque isSingleBend cae por el otro lado
            // (se filtra en el switch).
            let dx = 0, dy = 0;
            if (ev.code === 'ArrowUp') dy = -1;
            else if (ev.code === 'ArrowDown') dy = 1;
            else if (ev.code === 'ArrowLeft') dx = -1;
            else dx = 1;
            ev.preventDefault();
            stage.moveSelectedByGrid(dx, dy);
          }
          break;
        }
      }
    });
  }

  /**
   * Ejecuta un doblez desde el botón/tecla `buttonDir` ('right' = flecha
   * a la derecha de la toolbar; 'left' = flecha a la izquierda).
   * El sentido REAL (CW/CCW) depende del ángulo mundo del tramo de pin
   * ANTES de doblar:
   *   ángulo ∈ [0°, 180°]  (positivo): botón 'right' = CW, 'left' = CCW
   *   ángulo ∈ (-180°, 0°) (negativo): botón 'right' = CCW, 'left' = CW
   * Así los botones se comportan de forma consistente para el usuario
   * (derecha = "hacia la derecha en pantalla") independientemente de
   * cómo esté rotado el pin.
   */
  private doBendResolved(buttonDir: 'left' | 'right'): void {
    if (!this.stage) return;
    const world = this.stage.getSelectedBendWorldAngle();
    const resolved = resolveBendDirection(world, buttonDir);
    this.stage.bendSelected(resolved);
  }

  /**
   * Abre el diálogo que pregunta por el fondo de la imagen. Recuerda
   * la última opción elegida como preselección.
   */
  private openExportModal(): void {
    const backdrop = this.root.querySelector('[data-role="export-backdrop"]') as HTMLDivElement;
    const radio = this.root.querySelector<HTMLInputElement>(
      `[data-role="bg-choice"] input[value="${this.lastExportBackground}"]`,
    );
    if (radio) radio.checked = true;
    backdrop.hidden = false;
    radio?.focus();
  }

  private closeExportModal(): void {
    const backdrop = this.root.querySelector('[data-role="export-backdrop"]') as HTMLDivElement;
    backdrop.hidden = true;
    this.canvasContainer.focus();
  }

  /** Lee la opción marcada, cierra el diálogo y exporta. */
  private async confirmExport(): Promise<void> {
    const checked = this.root.querySelector<HTMLInputElement>(
      '[data-role="bg-choice"] input:checked',
    );
    const background = checked?.value === 'white' ? 'white' : 'transparent';
    this.lastExportBackground = background;
    this.closeExportModal();
    await this.exportImage(background);
  }

  /** Exporta el lienzo actual como PNG y lo descarga. */
  private async exportImage(background: 'transparent' | 'white' = 'transparent'): Promise<void> {
    if (!this.stage) return;
    try {
      // `toImageDataURL` es síncrono, así que el logo tiene que estar
      // decodificado ANTES o la primera exportación saldría con el
      // recuadro de marcador.
      const logo = await this.stage.ensureLogoLoaded();
      const dataUrl = this.stage.toImageDataURL({ background });
      const project = this.stage.getProject();
      // Preferimos el nombre elegido por el usuario para el .dbe (sin
      // extensión) para que la imagen comparta identidad con el esquema.
      const savedBase = this.savedFilename
        ? this.savedFilename.replace(/\.(dbe|json|png)$/i, '')
        : null;
      const base = savedBase
        ?? (project ? defaultFilename(project).replace(/\.dbe$/, '') : 'schema');
      const suffix = background === 'white' ? '-white' : '';
      const a = this.root.querySelector('[data-role="file-download"]') as HTMLAnchorElement;
      a.href = dataUrl;
      a.download = `${base}${suffix}.png`;
      a.click();
      // Si el logo no está, la imagen sale con el recuadro marcador.
      // Se dice explícitamente para que no parezca un fallo silencioso.
      this.setStatus(
        logo
          ? `Image exported (${base}${suffix}.png).`
          : `Image exported (${base}${suffix}.png) — logo not found, placeholder used. Drop your PNG at public/logo.png.`,
      );
    } catch (e) {
      this.setStatus(`Could not export image: ${(e as Error).message}`);
    }
  }

  private updateToggleButton(role: string, active: boolean): void {
    const btn = this.root.querySelector<HTMLButtonElement>(`[data-role="${role}"]`);
    if (!btn) return;
    // Fondo blanco siempre — el estado activo se marca sólo con el
    // borde naranja para no llenar la barra de manchas de color.
    btn.style.background = '';
    btn.style.borderColor = active ? 'var(--ui-orange)' : '';
  }

  /**
   * Estado de la toolbar durante la colocación de un cable:
   *   active=true  → todos los botones deshabilitados excepto
   *                  "Cable" (renombrado a "Cancelar"), que aborta.
   *   active=false → estado normal (rehidratado con updateSelectionUi).
   */
  private placingWireActive = false;

  private updatePlacingWireUi(active: boolean): void {
    this.placingWireActive = active;
    const toolbar = this.root.querySelector('[data-role="toolbar"]');
    if (!toolbar) return;
    const addWireBtn = toolbar.querySelector<HTMLButtonElement>('[data-action="add-wire"]');
    const buttons = toolbar.querySelectorAll<HTMLButtonElement>('button');
    if (active) {
      // Todos los botones de la toolbar deshabilitados, salvo el de
      // cable que pasa a hacer de "Cancelar".
      buttons.forEach((b) => {
        if (b === addWireBtn) return;
        b.disabled = true;
      });
      if (addWireBtn) {
        addWireBtn.disabled = false;
        addWireBtn.innerHTML = `${ICONS.cancel}<span class="btn-label">Cancel</span>`;
        addWireBtn.title = 'Cancel wire drawing (Esc)';
      }
    } else {
      // Restaurar estado normal. Los botones data-needs se re-evalúan
      // con la selección actual; los demás quedan habilitados.
      buttons.forEach((b) => { b.disabled = false; });
      if (addWireBtn) {
        addWireBtn.innerHTML = `${ICONS.wire}<span class="btn-label">Wire</span>`;
        addWireBtn.title = 'Add wire (W)';
      }
      this.updateSelectionUi(this.stage?.getSelection() ?? null);
    }
  }

  /** Análogo a placingWireActive pero para el modo soldadura. */
  private placingSolderActive = false;

  private updatePlacingSolderUi(active: boolean): void {
    this.placingSolderActive = active;
    const toolbar = this.root.querySelector('[data-role="toolbar"]');
    if (!toolbar) return;
    const addSolderBtn = toolbar.querySelector<HTMLButtonElement>('[data-action="add-solder"]');
    const buttons = toolbar.querySelectorAll<HTMLButtonElement>('button');
    if (active) {
      buttons.forEach((b) => {
        if (b === addSolderBtn) return;
        b.disabled = true;
      });
      if (addSolderBtn) {
        addSolderBtn.disabled = false;
        addSolderBtn.innerHTML = `${ICONS.cancel}<span class="btn-label">Cancel</span>`;
        addSolderBtn.title = 'Cancel solder placement (Esc)';
      }
    } else {
      buttons.forEach((b) => { b.disabled = false; });
      if (addSolderBtn) {
        addSolderBtn.innerHTML = `${solderIcon()}<span class="btn-label">Solder</span>`;
        addSolderBtn.title = 'Add solder joint at a bend point or endpoint (S)';
      }
      this.updateSelectionUi(this.stage?.getSelection() ?? null);
    }
  }

  private updateSelectionUi(selection: Selection): void {
    // Durante colocación de cable o soldadura la toolbar se controla
    // desde su updatePlacing…Ui; ignoramos cambios de selección para
    // no rehabilitar botones que deben estar bloqueados.
    if (this.placingWireActive || this.placingSolderActive) return;
    const editablePin = this.isSelectedPinEditable(selection);
    const buttons = this.root.querySelectorAll<HTMLButtonElement>('[data-needs]');
    buttons.forEach((b) => {
      const need = b.dataset['needs'];
      b.disabled = !matchesNeed(need, selection, editablePin);
    });
    // Cambia el texto del botón restaurar según selección.
    const restoreBtn = this.root.querySelector<HTMLButtonElement>('[data-role="btn-restore"]');
    if (restoreBtn) {
      const kind = selection?.kind;
      let text = 'Restore pin';
      if (kind === 'component') text = 'Restore component';
      else if (kind === 'wire' || kind === 'wire-bend') text = 'Restore wire';
      else if (kind === 'multi' && selection) {
        const hasComps = selection.component_uids.length > 0;
        const hasWires = (selection.wire_uids?.length ?? 0) > 0;
        if (hasComps && hasWires) text = 'Restore components and wires';
        else if (hasWires) text = 'Restore wires';
        else text = 'Restore components';
      }
      restoreBtn.innerHTML = `${ICONS.restore}<span class="btn-label">${text}</span>`;
    }
    // "Cut" button becomes "Split" for wire-bend selections.
    const cutBtn = this.root.querySelector<HTMLButtonElement>('[data-role="btn-cut"]');
    if (cutBtn) {
      const isWireBend = selection?.kind === 'wire-bend';
      cutBtn.innerHTML = `${ICONS.cut}<span class="btn-label">${isWireBend ? 'Split' : 'Cut'}</span>`;
      cutBtn.title = isWireBend
        ? 'Split the wire in two at this point'
        : 'Cut pin at this point';
    }
    this.setStatus(describeSelection(selection));
  }

  // Nota: antes existía un `updateBendIconRotation` que giraba el
  // dibujo del pin dentro de los botones de doblar para que coincidiese
  // con la orientación del pin real. Se quitó por decisión de diseño:
  // los iconos son ahora fijos, con una flecha recta arriba (izda/dcha)
  // que sólo indica la posición del botón (no el sentido real del
  // doblez, que se resuelve por resolveBendDirection en tiempo de uso).

  private isSelectedPinEditable(sel: Selection): boolean {
    if (!sel || (sel.kind !== 'pin' && sel.kind !== 'bend')) return false;
    // OJO: leer siempre desde el stage (fuente de verdad). Antes se
    // usaba `this.project.components`, pero ese objeto queda obsoleto
    // tras un undo/redo o una carga porque el stage sustituye el
    // proyecto internamente. Cuando pasaba eso, el componente
    // seleccionado no aparecía en `this.project`, el find devolvía
    // undefined y matchesNeed('bend') caía a false — con el efecto
    // visible de que "Cortar" (y otros botones que dependan de
    // editablePin) se quedaban en gris aunque el shortcut T sí
    // funcionaba (T va directo a stage.cutSelected sin pasar por
    // matchesNeed).
    const project = this.getProject();
    const inst = project.components.find((c) => c.uid === sel.component_uid);
    if (!inst) return false;
    const def = this.library.get(inst.def_id);
    if (!def) return false;
    const pin = def.pins.find((p) => p.id === sel.pin_id);
    if (!pin) return false;
    return !pin.locked && pin.bend_points_count > 0;
  }

  private mountStage(): void {
    const libraryUrl = this.getAttribute('component-library-url') ?? '/components/';
    this.library = new ComponentLibrary(libraryUrl);
    const logoUrl = this.getAttribute('logo-url');
    this.stage = new CanvasStage({
      container: this.canvasContainer,
      library: this.library,
      ...(logoUrl ? { logoUrl } : {}),
      onSelectionChange: (sel) => this.updateSelectionUi(sel),
      onOpenProperties: (uid) => this.openPropertiesPanel(uid),
      onZoomChange: (pct) => this.updateZoomUi(pct),
      onPlacingWireChange: (active) => this.updatePlacingWireUi(active),
      onPlacingSolderChange: (active) => this.updatePlacingSolderUi(active),
    });
    this.wirePropertiesPanel();
    // Cuadrícula visible por defecto: pintar botón como activo.
    this.updateToggleButton('btn-grid', true);
  }

  private wirePropertiesPanel(): void {
    const panel = this.root.querySelector('[data-role="props-panel"]') as HTMLDivElement;
    const head = this.root.querySelector('[data-role="props-head"]') as HTMLDivElement;
    const closeBtn = panel.querySelector('[data-action="close-props"]') as HTMLButtonElement;
    const valueInput = this.root.querySelector('[data-role="props-value"]') as HTMLInputElement;

    closeBtn.addEventListener('click', () => (panel.hidden = true));

    valueInput.addEventListener('input', () => {
      const uid = panel.dataset['uid'];
      if (uid) this.stage?.setInstanceValue(uid, valueInput.value);
    });

    // Arrastre por la cabecera.
    let dragOffset: { x: number; y: number } | null = null;
    head.addEventListener('mousedown', (e) => {
      if ((e.target as HTMLElement).tagName === 'BUTTON') return;
      const r = panel.getBoundingClientRect();
      dragOffset = { x: e.clientX - r.left, y: e.clientY - r.top };
      e.preventDefault();
    });
    document.addEventListener('mousemove', (e) => {
      if (!dragOffset) return;
      const parentR = (panel.offsetParent as HTMLElement).getBoundingClientRect();
      const x = e.clientX - parentR.left - dragOffset.x;
      const y = e.clientY - parentR.top - dragOffset.y;
      panel.style.left = `${Math.max(0, x)}px`;
      panel.style.top = `${Math.max(0, y)}px`;
    });
    document.addEventListener('mouseup', () => {
      dragOffset = null;
    });
  }

  private openPropertiesPanel(uid: string): void {
    // Mismo problema que en isSelectedPinEditable: `this.project`
    // queda obsoleto tras undo/redo/load y el componente clicado no
    // aparecería en él, así que el panel se cerraba en silencio (era
    // el motivo de que "no se abriera Propiedades" en algunos ICs).
    // Se lee siempre desde el stage.
    const project = this.getProject();
    const inst = project.components.find((c) => c.uid === uid);
    if (!inst) return;
    const def = this.library.get(inst.def_id);
    if (!def) return;
    const panel = this.root.querySelector('[data-role="props-panel"]') as HTMLDivElement;
    const valueInput = this.root.querySelector('[data-role="props-value"]') as HTMLInputElement;
    const uidEl = this.root.querySelector('[data-role="props-uid"]') as HTMLDivElement;
    const extraEl = this.root.querySelector('[data-role="props-extra"]') as HTMLDivElement;
    panel.dataset['uid'] = uid;
    valueInput.value = inst.value ?? def.electrical?.default_value ?? def.appearance.label?.text ?? '';
    uidEl.textContent = `${def.name} · ${uid}`;

    // Propiedades dinámicas (enum): un select por cada PropertyDef.
    extraEl.innerHTML = '';
    for (const prop of def.properties ?? []) {
      const wrap = document.createElement('label');
      wrap.textContent = prop.label;
      const sel = document.createElement('select');
      for (const opt of prop.options) {
        const o = document.createElement('option');
        o.value = opt;
        o.textContent = opt;
        sel.appendChild(o);
      }
      sel.value = inst.properties?.[prop.key] ?? prop.default;
      sel.style.padding = '4px 6px';
      sel.style.font = 'inherit';
      sel.style.fontSize = '13px';
      sel.addEventListener('change', () => {
        this.stage?.setInstanceProperty(uid, prop.key, sel.value);
      });
      wrap.appendChild(sel);
      extraEl.appendChild(wrap);
    }

    panel.hidden = false;
  }

  private async bootstrap(): Promise<void> {
    const projectUrl = this.getAttribute('project-url');
    if (projectUrl) {
      try {
        const res = await fetch(projectUrl);
        if (res.ok) {
          this.project = (await res.json()) as Project;
        }
      } catch {
        // Silenciar: se queda con proyecto vacío.
      }
    }
    this.stage?.setProject(this.project);
  }

  private setStatus(text: string): void {
    const status = this.root.querySelector('[data-role="status-text"]');
    if (status) status.textContent = text;
  }

  private updateZoomUi(percent: number): void {
    const el = this.root.querySelector('[data-role="zoom-indicator"]');
    if (el) el.textContent = `zoom ${percent} %`;
  }

  // ---------- persistencia ----------

  private async saveProjectToFile(): Promise<void> {
    if (!this.stage) return;
    const project = this.stage.getProject();
    if (!project) {
      this.setStatus('No project to save.');
      return;
    }
    const payload = serializeProject(project, this.library);
    const json = JSON.stringify(payload, null, 2);
    const suggestedName = this.savedFilename ?? defaultFilename(project);
    // Preferimos File System Access API cuando está disponible
    // (Chromium): permite sobrescribir sin re-elegir ruta y devuelve
    // un handle. Si no, caemos al método <a download>.
    const anyWin = window as unknown as {
      showSaveFilePicker?: (opts: unknown) => Promise<FileSystemFileHandle>;
    };
    try {
      if (typeof anyWin.showSaveFilePicker === 'function') {
        const handle = await anyWin.showSaveFilePicker({
          suggestedName,
          types: [{
            description: 'DeadBug schematic',
            accept: { 'application/json': ['.json', '.dbe'] },
          }],
        });
        const writable = await (handle as unknown as { createWritable(): Promise<{ write(d: string): Promise<void>; close(): Promise<void> }> }).createWritable();
        await writable.write(json);
        await writable.close();
        // Recordamos el nombre real elegido por el usuario para
        // futuras acciones de "Guardar" y "Exportar imagen".
        const chosenName = (handle as unknown as { name?: string }).name;
        if (chosenName) this.savedFilename = chosenName;
        this.setStatus(`Schematic saved (${this.savedFilename ?? suggestedName}).`);
        return;
      }
    } catch (e) {
      const err = e as DOMException;
      if (err?.name === 'AbortError') return; // usuario canceló
      console.warn('showSaveFilePicker falló, usando fallback de descarga:', e);
    }
    // Fallback universal: descarga vía <a download>.
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = this.root.querySelector('[data-role="file-download"]') as HTMLAnchorElement;
    a.href = url;
    a.download = suggestedName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    // Recordamos el nombre elegido (aunque en el fallback no sabemos
    // si el usuario lo cambió al guardar; usamos la sugerencia).
    this.savedFilename = suggestedName;
    this.setStatus(`Schematic downloaded (${suggestedName}).`);
  }

  private async openProjectFromFile(): Promise<void> {
    const anyWin = window as unknown as {
      showOpenFilePicker?: (opts: unknown) => Promise<FileSystemFileHandle[]>;
    };
    try {
      if (typeof anyWin.showOpenFilePicker === 'function') {
        const [handle] = await anyWin.showOpenFilePicker({
          multiple: false,
          types: [{
            description: 'DeadBug schematic',
            accept: { 'application/json': ['.json', '.dbe'] },
          }],
        });
        if (!handle) return;
        const file = await (handle as unknown as { getFile(): Promise<File> }).getFile();
        await this.loadProjectFile(file);
        return;
      }
    } catch (e) {
      const err = e as DOMException;
      if (err?.name === 'AbortError') return;
      console.warn('showOpenFilePicker falló, usando <input type=file>:', e);
    }
    // Fallback: input type=file.
    const picker = this.root.querySelector('[data-role="file-picker"]') as HTMLInputElement;
    picker.click();
  }

  private async loadProjectFile(file: File): Promise<void> {
    if (!this.stage) return;
    try {
      const text = await file.text();
      const project = deserializeProject(text, this.library);
      this.project = project;
      this.stage.setProject(project);
      // Recordamos el nombre del fichero para que subsiguientes
      // "Guardar" y "Exportar imagen" usen el mismo nombre.
      this.savedFilename = file.name;
      this.setStatus(`Schematic opened: ${file.name}`);
    } catch (e) {
      const msg = e instanceof SerializationError ? e.message : (e as Error).message;
      console.error('Error opening schema:', e);
      this.setStatus(`Could not open schematic: ${msg}`);
      alert(`Could not open schematic:\n${msg}`);
    }
  }

  private async openLibraryModal(): Promise<void> {
    const backdrop = this.root.querySelector('[data-role="library-backdrop"]') as HTMLDivElement;
    const list = this.root.querySelector('[data-role="component-list"]') as HTMLUListElement;
    const addBtn = this.root.querySelector('[data-role="add-btn"]') as HTMLButtonElement;

    list.innerHTML = '<li class="meta">Loading…</li>';
    backdrop.hidden = false;
    addBtn.disabled = true;

    let entries: LibraryIndexEntry[] = [];
    try {
      const index = await this.library.loadIndex();
      // Orden: 1) genéricos antes que específicos.
      //        2) dentro de cada grupo: pasivos → discretos → ICs → otros.
      //        3) dentro de cada categoría: alfabético por nombre.
      const catRank = (c: string): number => {
        switch (c) {
          case 'passive':
            return 0;
          case 'transistor':
          case 'diode':
          case 'led':
            return 1;
          case 'ic':
            return 2;
          default:
            return 3;
        }
      };
      entries = index.components.slice().sort((a, b) => {
        const ga = a.generic ? 0 : 1;
        const gb = b.generic ? 0 : 1;
        if (ga !== gb) return ga - gb;
        const ca = catRank(a.category);
        const cb = catRank(b.category);
        if (ca !== cb) return ca - cb;
        return a.name.localeCompare(b.name);
      });
    } catch (err) {
      list.innerHTML = `<li class="meta">Error: ${(err as Error).message}</li>`;
      return;
    }

    list.innerHTML = '';
    let currentPick: LibraryIndexEntry | null = null;

    const selectEntry = (li: HTMLLIElement, entry: LibraryIndexEntry) => {
      list.querySelectorAll('li.selected').forEach((el) => el.classList.remove('selected'));
      li.classList.add('selected');
      currentPick = entry;
      this.lastPickedDefId = entry.id;
      addBtn.disabled = false;
    };

    for (const entry of entries) {
      const li = document.createElement('li');
      li.innerHTML = `
        <img class="thumb" alt="" data-role="thumb" />
        <div class="info">
          <div class="name">${escapeHtml(entry.name)}</div>
          <div class="meta">${escapeHtml(entry.category)} · ${escapeHtml(entry.id)}</div>
        </div>
      `;
      // La miniatura se genera bajo demanda para no bloquear el
      // renderizado inicial de una lista larga. Si la def ya está
      // cargada (cache), se pinta al instante; si no, se carga en
      // background y se rellena cuando llegue.
      const thumbEl = li.querySelector('[data-role="thumb"]') as HTMLImageElement;
      const cachedDef = this.library.get(entry.id);
      if (cachedDef) {
        try { thumbEl.src = this.previewCache.get(cachedDef); } catch { /* silenciar */ }
      } else {
        this.library.loadFromIndex(entry)
          .then((d) => { try { thumbEl.src = this.previewCache.get(d); } catch { /* ignorar */ } })
          .catch(() => { /* si falla, la miniatura queda vacía */ });
      }
      li.addEventListener('click', () => selectEntry(li, entry));
      li.addEventListener('dblclick', () => {
        selectEntry(li, entry);
        void this.confirmLibraryPick();
      });
      list.appendChild(li);

      // Preselecciona el último añadido (o el primero si no hay historial).
      if (this.lastPickedDefId === entry.id || (!currentPick && !this.lastPickedDefId)) {
        selectEntry(li, entry);
      }
    }

    if (!currentPick && entries[0]) {
      const firstLi = list.querySelector('li');
      if (firstLi) selectEntry(firstLi as HTMLLIElement, entries[0]);
    }
  }

  private closeLibraryModal(): void {
    const backdrop = this.root.querySelector('[data-role="library-backdrop"]') as HTMLDivElement;
    backdrop.hidden = true;
  }

  private async confirmLibraryPick(): Promise<void> {
    if (!this.lastPickedDefId) return;
    const index = await this.library.loadIndex();
    const entry = index.components.find((c) => c.id === this.lastPickedDefId);
    if (!entry) return;
    this.closeLibraryModal();
    try {
      const def = await this.library.loadFromIndex(entry);
      this.stage?.startPlacement(def);
      this.setStatus(`Placing ${def.name} — click to drop, Esc to cancel.`);
    } catch (err) {
      this.setStatus(`Error loading ${entry.name}: ${(err as Error).message}`);
    }
  }

  /** API imperativa. El stage es la fuente de verdad: deshacer/rehacer
   *  sustituyen el objeto proyecto, así que `this.project` puede quedar
   *  obsoleto. */
  getProject(): Project {
    return this.stage?.getProject() ?? this.project;
  }

  setProject(project: Project): void {
    this.project = project;
    this.stage?.setProject(project);
  }
}

/** Registra el custom element si aún no lo está. Idempotente. */
export function registerDeadBugEditor(tagName = 'deadbug-editor'): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get(tagName)) return;
  customElements.define(tagName, DeadBugEditorElement);
}

function matchesNeed(need: string | undefined, sel: Selection, editablePin: boolean): boolean {
  if (!need || !sel) return false;
  switch (need) {
    case 'component':
      // Un componente, cualquier drill-down o multi-selección, o
      // un cable (o su punto de doblez) → habilitan las operaciones
      // "de elemento" (rotar, eliminar, mover z, etc.).
      return (
        sel.kind === 'component' ||
        sel.kind === 'pin' ||
        sel.kind === 'bend' ||
        sel.kind === 'multi' ||
        sel.kind === 'wire' ||
        sel.kind === 'wire-bend'
      );
    case 'pin':
      return sel.kind === 'pin' && editablePin;
    case 'bend':
      // Un punto de doblez de pin o de cable.
      return (sel.kind === 'bend' && editablePin) || sel.kind === 'wire-bend';
    case 'pin-or-bend':
      return (sel.kind === 'pin' || sel.kind === 'bend') && editablePin;
    case 'restorable':
      // Componente completo, multi, cable o su bend → siempre.
      if (
        sel.kind === 'component' ||
        sel.kind === 'multi' ||
        sel.kind === 'wire' ||
        sel.kind === 'wire-bend'
      ) return true;
      return (sel.kind === 'pin' || sel.kind === 'bend') && editablePin;
    case 'deletable':
      // Cualquier cosa borrable: incluye soldadura además de todo lo
      // que 'component' cubría.
      return (
        sel.kind === 'component' ||
        sel.kind === 'pin' ||
        sel.kind === 'bend' ||
        sel.kind === 'multi' ||
        sel.kind === 'wire' ||
        sel.kind === 'wire-bend' ||
        sel.kind === 'solder'
      );
    default:
      return false;
  }
}

/**
 * Traduce la dirección del BOTÓN (visual, 'left'/'right' desde el
 * punto de vista del usuario en pantalla) a la dirección interna del
 * `bendOp` según el ángulo mundo del tramo de pin ANTES de doblar.
 *
 * El ángulo devuelto por Stage viene de `Math.atan2` en Y-DOWN
 * (convención Konva). El usuario piensa en la convención matemática
 * habitual, con Y-UP → "arriba" es positivo. Se negan el signo Y
 * (equivale a negar el ángulo) para trabajar en esa convención.
 *
 * Regla sobre el ángulo YA en Y-up:
 *   [0°, 180°]  → "positivo": right = 'right' (CW), left = 'left' (CCW)
 *   (-180°, 0°) → "negativo": invertido
 *
 * Efecto visual esperado:
 *   pin →   right button → tip curva HACIA ABAJO-DERECHA
 *   pin ←   right button → tip curva HACIA ARRIBA-IZQUIERDA
 *   pin ↑   right button → tip curva HACIA ARRIBA-DERECHA
 *   pin ↓   right button → tip curva HACIA ABAJO-DERECHA
 * (i.e., el tip siempre gira "por el lado derecho" desde la
 * perspectiva del observador cuando se pulsa la flecha derecha).
 */
function resolveBendDirection(
  worldAngleDeg: number | null,
  buttonDir: 'left' | 'right',
): 'left' | 'right' {
  if (worldAngleDeg === null) return buttonDir;
  // Konva Y-down → math Y-up, y normalizado a [0, 360) para que ±180°
  // (izquierda) caiga siempre en la mitad "positiva" (0..180]).
  const yUpAngle = -worldAngleDeg;
  const norm = ((yUpAngle % 360) + 360) % 360;
  const positive = norm >= 0 && norm <= 180;
  if (positive) return buttonDir;
  return buttonDir === 'right' ? 'left' : 'right';
}

function defaultFilename(project: Project): string {
  const base = (project.metadata.name || 'untitled')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '');
  const safe = base || 'untitled';
  return `${safe}.dbe`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function describeSelection(sel: Selection): string {
  if (!sel) return 'No selection.';
  switch (sel.kind) {
    case 'component':
      return `Component selected (${sel.component_uid}).`;
    case 'pin':
      return `Pin ${sel.pin_id} selected.`;
    case 'bend':
      return `Bend point ${sel.bend_index} of pin ${sel.pin_id} selected.`;
    case 'multi': {
      const nc = sel.component_uids.length;
      const nw = sel.wire_uids?.length ?? 0;
      if (nw === 0) return `${nc} components selected.`;
      if (nc === 0) return `${nw} wires selected.`;
      return `${nc} components and ${nw} wires selected.`;
    }
    case 'wire':
      return 'Wire selected.';
    case 'wire-bend':
      return `Bend point ${sel.bend_index} of wire selected.`;
    case 'solder':
      return 'Solder joint selected.';
  }
}
